import { useEffect, useState } from "react";

import {
  DatePicker,
  RadioButton,
  TextField,
  Select,
  DocumentList,
  DocumentsModal,
  Button,
  InfoPanel,
} from "@king-ict/eupisi-ui/components";
import { UploadFile } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { DEFAULT_REQUEST } from "./constants";
import { getInstitutionById } from "../../api/Institution/InstitutionApi";
import {
  getRequestDocument,
  getEnrollmentDecision,
  saveEnrollmentDecision,
  getForm7,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
  TemporaryExemptionSchoolYearSelect,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { ValidationFailure, Role } from "../../models";
import { Institution } from "../../models/Institution";
import {
  Request,
  RequestStatus,
  RequestType,
  DocumentType,
  EnrollmentDecision,
  EnrollmentDecisionSolution,
  EnrollmentDecisionConclusion,
  Form7,
} from "../../models/Request";
import { Attachment } from "../../models/Shared/Attachment";
import { requestAdminDetailsUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { MsgUtils } from "../../utils";
import { getDefaultSolutionIdFromForm7 } from "./utils/initialStateGeneration";

export function EnrollmentDecisionEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const createDefaultForm7 = (): Form7 => {
    return {
      id: 0,
      requestId: +id,
      sessionDate: new Date(),
      isEarlyEnrollmentApproved: false,
      isEarlyEnrollmentRejected: false,
      isEnrollmentPostponement: false,
      isTemporaryExemptionApproved: false,
      isTemporaryExemptionRejected: false,
      isAppropriateProgram: false,
      isPreparatoryCourse: false,
      hasDifficulties: false,
      request: DEFAULT_REQUEST,
      committeesForDocument: [],
    };
  };

  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const [form7, setForm7] = useState<Form7>(createDefaultForm7());
  const [instutition, setInstitution] = useState<Institution | undefined>();

  useEffect(() => {
    if (!decision.solutionId && form7) {
      setDecision({
        id: 0,
        requestId: +id,
        solutionId: getDefaultSolutionIdFromForm7(form7),
      });
    }
  }, [form7]);

  const createDefaultDecision = (): EnrollmentDecision => {
    return {
      id: 0,
      requestId: +id,
      solutionId: getDefaultSolutionIdFromForm7(form7),
    };
  };
  const [decision, setDecision] = useState<EnrollmentDecision>(
    createDefaultDecision()
  );

  const [lastSolutionId, setLastSolutionId] = useState<
    EnrollmentDecisionSolution | undefined
  >(getDefaultSolutionIdFromForm7(form7));

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();

  useEffect(() => {
    (async () => {
      setIsEditing(true); // Assume we don't get one.
      if (id && id !== "0") {
        var result = await getEnrollmentDecision(+id);
        if (result) {
          setDecision(result);
          setIsEditing(false);
          if (result.request) {
            setItem(result.request);
          }
        } else {
          var form7 = await getForm7(+id);
          if (form7) {
            setForm7(form7);
            if (form7.request) setItem(form7.request);
          }
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    (async () => {
      if (form7?.institutionId && item?.schoolYearId) {
        const inst = await getInstitutionById(
          form7.institutionId,
          item.schoolYearId
        );
        if (inst) setInstitution(inst);
      }
    })();
  }, [form7.institutionId, item.schoolYearId]);

  useEffect(() => {
    (async () => {
      if (item?.enrollmentInstitutionId && item?.schoolYearId) {
        const inst = await getInstitutionById(
          item.enrollmentInstitutionId,
          item.schoolYearId
        );
        if (inst) setInstitution(inst);
      }
    })();
  }, [item.enrollmentInstitutionId, item.schoolYearId]);

  const handleEditingClick = () => {
    setIsEditing(true);
  };
  const handleDateOfIssueChanged = (val?: Date) => {
    const d: EnrollmentDecision = {
      ...decision,
      dateOfIssue: val,
    };
    handleUpdate(d);
  };
  const handleDateOfDeliveryChanged = (val?: Date) => {
    const d: EnrollmentDecision = {
      ...decision,
      dateOfDelivery: val,
    };
    handleUpdate(d);
  };
  const handleSolutionIdChange = (val?: EnrollmentDecisionSolution) => {
    if (val !== undefined) {
      setLastSolutionId(val);
    }

    const d: EnrollmentDecision = {
      ...decision,
      solutionId: val,
      conclusionId: undefined,
    };

    handleUpdate(d);
  };
  const handleSolutionChange = (val: boolean) => {
    const d: EnrollmentDecision = {
      ...decision,
      solutionId: val
        ? lastSolutionId ?? getDefaultSolutionIdFromForm7(form7)
        : undefined,
      conclusionId: val
        ? undefined
        : EnrollmentDecisionConclusion.RegularEnrollmentApprovedProposalRejected,
    };

    handleUpdate(d);
  };
  const handleConclusionIdChange = (val?: EnrollmentDecisionConclusion) => {
    const d: EnrollmentDecision = {
      ...decision,
      conclusionId: val,
    };
    handleUpdate(d);
  };
  const handleConclusionChange = (val: boolean) => {
    const d: EnrollmentDecision = {
      ...decision,
      solutionId: val
        ? undefined
        : EnrollmentDecisionSolution.EarlyEnrollmentApproved,
      conclusionId: val
        ? EnrollmentDecisionConclusion.RegularEnrollmentApprovedProposalRejected
        : undefined,
    };
    handleUpdate(d);
  };

  const handleTemporaryExemptionUntilSchoolYearIdChange = (val?: number) => {
    handleUpdate({ ...decision, temporaryExemptionUntilSchoolYearId: val });
  };

  const handleClassificationChange = (val?: string) => {
    const d: EnrollmentDecision = {
      ...decision,
      classification: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleAssociationNumberChange = (val?: string) => {
    const d: EnrollmentDecision = {
      ...decision,
      associationNumber: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleUploadDocumentClick = () => {
    setShowUploadModal(true);
  };
  /**
   * Reads the file binary data and pushes the listed files to the uploadDocument state variable.
   * @param files
   */
  const handleFileChange = async (files: File[]) => {
    var uploadedFiles: UploadFile[] = [];
    if (files && files.length > 0) {
      files.forEach((file) => {
        const fileReader = new FileReader();
        fileReader.readAsBinaryString(file);
        var uploadedFile = {
          fileName: file.name,
          fileData: fileReader.result,
        } as UploadFile;
        uploadedFiles.push(uploadedFile);
      });
    }
    const d: EnrollmentDecision = {
      ...decision,
      uploadFiles:
        uploadedFiles && uploadedFiles.length > 0 ? uploadedFiles : undefined,
    };
    handleUpdate(d);
  };
  /**
   * Pushes the listed files to the uploadDocument state variable, sets
   * the appropriate collection on the item, and closes the modal.
   * @param files
   */
  const handleUploadFileChange = (files: UploadFile[]) => {
    const combined = files.concat(decision.uploadFiles ?? []);
    const d: EnrollmentDecision = {
      ...decision,
      uploadFiles: combined && combined.length > 0 ? combined : undefined,
    };
    setShowUploadModal(false);
    handleUpdate(d);
  };
  /**
   * Removes the specified doc (existing attachment) from the specified item collection.
   * @param doc
   */
  const handleRemoveAttachment = (doc: Attachment) => {
    if (doc && doc.id) {
      const attachmentList = decision.attachments?.filter(
        (v) => v.id !== doc.id
      );
      const d: EnrollmentDecision = {
        ...decision,
        attachments:
          attachmentList && attachmentList.length > 0
            ? attachmentList
            : undefined,
      };
      handleUpdate(d);
    }
  };
  const handleDocumentDownload = async (
    doc: Attachment,
    type: DocumentType
  ) => {
    if (doc && doc.id) {
      await getRequestDocument(doc.id, item.id, type);
    }
  };
  const handleUpdate = (item: EnrollmentDecision) => {
    setDecision(item);
    dispatch(setIsEdited());
  };
  const getEnrollmentDecisionSolution = (form7: Form7) => {
    let cb = MsgUtils.getEnumToCodeBook(
      EnrollmentDecisionSolution,
      "enrollmentDecisionSolution"
    );
    if (form7?.isEarlyEnrollmentApproved) return [cb[0]];
    else if (form7?.isEarlyEnrollmentRejected) return [cb[1]];
    else if (form7?.isEnrollmentPostponement) return [cb[2]];
    else if (form7?.isTemporaryExemptionApproved) return [cb[3]];
    else if (form7?.isTemporaryExemptionRejected) return [cb[4]];
    else if (form7?.isAppropriateProgram) return [cb[5]];
    else return cb;
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={
          !isEditing &&
          item.statusId === RequestStatus.OpinionWithRecommendation &&
          (auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk ||
            auth.currentRole?.role === Role.CountyAdministrator ||
            auth.currentRole?.role === Role.CountyLeader ||
            (item.countyCommitteeId &&
              auth
                .getCommitteeMembershipByCommitteeId(item.countyCommitteeId)
                ?.some((v) => v.isPresident)))
            ? "show"
            : "hide"
        }
        pageTitle={t("enrollmentDecision.pageTitle")}
        onClickEdit={handleEditingClick}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard>
        <FormCard>
          <Row>
            <Col md={3}>
              <RadioButton
                ariaLabel={t("enrollmentDecision.solution")}
                label={t("enrollmentDecision.solution")}
                name={nameof<typeof decision>("solutionId")}
                checked={decision.solutionId !== undefined}
                onChange={(e) => handleSolutionChange(e.target.checked)}
                disabled={!isEditing}
              />
            </Col>
            <Col md={9}>
              <Select
                label={""}
                name={nameof<typeof decision>("solutionId")}
                value={decision.solutionId}
                onChange={(e) =>
                  handleSolutionIdChange(Number.parseInt(e.target.value))
                }
                selectData={codeBookToSelectData(
                  getEnrollmentDecisionSolution(form7)
                )}
                errorText={
                  errors?.find((e) => e.propertyName === "SolutionId.Value")
                    ?.message
                }
                disabled={!isEditing || !decision.solutionId}
              />
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <RadioButton
                ariaLabel={t("enrollmentDecision.conclusion")}
                label={t("enrollmentDecision.conclusion")}
                name={nameof<typeof decision>("conclusionId")}
                checked={decision.conclusionId !== undefined}
                onChange={(e) => handleConclusionChange(e.target.checked)}
                disabled={
                  !isEditing ||
                  item.type === RequestType.EarlyEnrollment ||
                  item.type === RequestType.TemporaryExemption
                }
              />
            </Col>
            <Col md={9}>
              <Select
                label={""}
                name={nameof<typeof decision>("conclusionId")}
                value={decision.conclusionId}
                onChange={(e) =>
                  handleConclusionIdChange(Number.parseInt(e.target.value))
                }
                selectData={codeBookToSelectData(
                  MsgUtils.getEnumToCodeBook(
                    EnrollmentDecisionConclusion,
                    "enrollmentDecisionConclusion"
                  )
                )}
                errorText={
                  errors?.find((e) => e.propertyName === "ConclusionId.Value")
                    ?.message
                }
                disabled={!isEditing || !decision.conclusionId}
              />
            </Col>
          </Row>
          {decision.solutionId ===
            EnrollmentDecisionSolution.TemporaryExemptionApproved && (
            <Row>
              <Col md={6}>
                <TemporaryExemptionSchoolYearSelect
                  value={decision.temporaryExemptionUntilSchoolYearId}
                  previouslySelectedSchoolYear={
                    decision.temporaryExemptionUntilSchoolYear
                  }
                  schoolYearId={item.schoolYearId}
                  isEditing={isEditing}
                  errors={errors}
                  onChange={handleTemporaryExemptionUntilSchoolYearIdChange}
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col md={6}>
              <TextField
                label={t("decisionProposal.classification")}
                name={nameof<typeof decision>("classification")}
                value={decision.classification}
                onChange={(e) => handleClassificationChange(e.target.value)}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("Classification")
                  )?.message
                }
              />
            </Col>
            <Col md={6}>
              <TextField
                label={t("decisionProposal.associationNumber")}
                name={nameof<typeof decision>("associationNumber")}
                value={decision.associationNumber}
                onChange={(e) => handleAssociationNumberChange(e.target.value)}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("AssociationNumber")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <DatePicker
                label={t("enrollmentDecision.dateOfIssue")}
                name={nameof<typeof decision>("dateOfIssue")}
                onChange={(e) =>
                  handleDateOfIssueChanged(
                    e.target.value ? new Date(e.target.value) : undefined
                  )
                }
                value={decision.dateOfIssue}
                minYear={new Date().getFullYear()}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) => e.propertyName?.startsWith("DateOfIssue"))
                    ?.message
                }
              />
            </Col>
            <Col md={6}>
              <DatePicker
                label={t("enrollmentDecision.dateOfDelivery")}
                name={nameof<typeof decision>("dateOfDelivery")}
                onChange={(e) =>
                  handleDateOfDeliveryChanged(
                    e.target.value ? new Date(e.target.value) : undefined
                  )
                }
                value={decision.dateOfDelivery}
                minYear={new Date().getFullYear()}
                minDate={
                  decision.dateOfIssue
                    ? new Date(decision.dateOfIssue)
                    : new Date()
                }
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("DateOfDelivery")
                  )?.message
                }
              />
            </Col>
          </Row>
        </FormCard>
        <FormCard cardTitle={t("form7.institution")}>
          <Row>
            <Col md={3}>{t("common.schoolCandidateModel.institutionList")}</Col>
            <Col md={3}>{t("common.schoolCandidateModel.countyList")}</Col>
            <Col md={3}>
              {t("common.schoolCandidateModel.municipalityList")}
            </Col>
            <Col md={3}>{t("common.schoolCandidateModel.townList")}</Col>
          </Row>
          {instutition && (
            <Row>
              <Col md={3}>{instutition?.name}</Col>
              <Col md={3}>{instutition?.address?.county}</Col>
              <Col md={3}>{instutition?.address?.municipality}</Col>
              <Col md={3}>{instutition?.address?.town}</Col>
            </Row>
          )}
          {!instutition && (
            <Row>
              <Col md={3}>-</Col>
            </Row>
          )}
        </FormCard>
        <FormCard cardTitle={t("enrollmentDecision.attachments")}>
          {errors?.find((e) => e.propertyName === "Documents") && (
            <Row>
              <Col>
                <InfoPanel
                  color={"danger"}
                  message={
                    errors?.find((e) => e.propertyName === "Documents")
                      ?.message ?? ""
                  }
                  fullWidth
                  dismissible
                  showIcon
                />
              </Col>
            </Row>
          )}
          {decision.form7Attachment && (
            <Row>
              <Col md={4}>
                <DocumentList
                  documents={[decision.form7Attachment]}
                  onDocumentClick={(doc) =>
                    handleDocumentDownload(doc, DocumentType.Form7)
                  }
                  removable={false}
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col md={4}>
              <DocumentList
                documents={decision.attachments ?? []}
                onDocumentClick={(doc) =>
                  handleDocumentDownload(doc, DocumentType.EnrollmentDecision)
                }
                onRemoveClick={(doc) => handleRemoveAttachment(doc)}
                removable={isEditing}
              />
            </Col>
            <Col md={4}>
              <Button
                color="primary"
                onClick={handleUploadDocumentClick}
                size="sm"
                disabled={!isEditing}
              >
                {t("request.documentUpload")}
              </Button>
            </Col>
            <Col md={4}>
              <DocumentList
                documents={decision.uploadFiles ?? []}
                removable={false}
              />
            </Col>
          </Row>
        </FormCard>
      </FormCard>
      <DocumentsModal
        title={t("accessibility.documentModal.title")}
        infoPanelText={t("accessibility.documentModal.info")}
        types={t("accessibility.documentModal.types")}
        allDocuments={decision.uploadFiles ?? []}
        maxSize={10}
        filesAction={handleFileChange}
        open={showUploadModal}
        onConfirm={handleUploadFileChange}
        onClose={() => {
          setShowUploadModal(false);
        }}
        hideOldDocuments={true}
        btnCancelText={t("common.quit")}
        btnOkText={t("common.addNew")}
      />
      <ToolbarEdit
        showCancel={"show"}
        showSave={isEditing ? "show" : "hide"}
        redirectUrlSaveOK={() => requestAdminDetailsUrl(+id)}
        handleSave={async () => saveEnrollmentDecision(decision)}
        setSaveErrors={setErrors}
        showCustom={false}
      />
    </ContentArea>
  );
}
